import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Copy,
  Eraser,
  Loader2,
  Plus,
  RefreshCw,
  Redo2,
  ScrollText,
  Sparkles,
  Undo2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import {
  generateReportCardBuilderComment,
  getReportCardBuilderData,
  getRcbLrpContext,
  getRcbRecentlyTaughtExpectations,
  unmarkActivityTaught,
  dismissCoverage,
} from "../lib/api";
import LiveReportsPanel from "./LiveReportsPanel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { ExternalLink, FileText, Wand2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { hasLiveReportComments } from "../lib/tier";

const GRADE_OPTIONS = [
  "Grade 4",
  "Grade 5",
  "Grade 6",
  "Grade 7",
  "Grade 8",
];

// Phase 113.0 (Feb 2026) — the three real Ontario elementary reports.
// Stored values are the snake_case keys; user-facing label is the
// Title-case version. The selected report drives mark inputs, default
// subject visibility (Progress = Math+Lang only by default), and the
// LRP context window pulled from the teacher's plan.
const REPORT_TYPES = [
  { value: "progress_report", label: "Progress Report" },
  { value: "term_1", label: "Term 1" },
  { value: "term_2", label: "Term 2" },
];
const REPORT_TYPE_LABEL = Object.fromEntries(
  REPORT_TYPES.map((r) => [r.value, r.label])
);

// Subjects that ALWAYS appear by default on the Progress Report tab
// strip (Math + Language). Every other subject is hidden until the
// teacher flips its "Flag: Progressing With Difficulty" toggle. The
// match is against `BuilderSubject.subject` so this list uses backend
// canonical names.
const PROGRESS_REPORT_DEFAULT_SUBJECTS = new Set([
  "Language",
  "Mathematics",
]);

// Progress Report mark input — three descriptors, no marks language.
const PROGRESS_DESCRIPTORS = [
  { value: "PWD", label: "Progressing With Difficulty" },
  { value: "PW", label: "Progressing Well" },
  { value: "PVW", label: "Progressing Very Well" },
];

// Achievement options for grades 4-6. Letters map to Ontario levels but we
// keep them as A/B/C/D/R per the spec's report-card front-end labelling.
const LEVEL_AB_OPTIONS = [
  { value: "A", label: "A — Exceeding (Lv 4)" },
  { value: "B", label: "B — Meeting (Lv 3)" },
  { value: "C", label: "C — Approaching (Lv 2)" },
  { value: "D", label: "D — Below (Lv 1)" },
  { value: "R", label: "R — Remediation" },
];

const LEARNING_SKILL_OPTIONS = [
  { value: "E", label: "E — Excellent" },
  { value: "G", label: "G — Good" },
  { value: "S", label: "S — Satisfactory" },
  { value: "N", label: "N — Needs Improvement" },
];

const FIELD_CLASS =
  "bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus:ring-2 focus:ring-[#96ded1] focus-visible:ring-[#96ded1] rounded-md h-11";

// Phase 113.B — Term 1 / Term 2 use letter grades for Grades 1–6 and
// percentages for Grades 7–8 (per the Ontario elementary report
// standard). Progress Report ALWAYS uses descriptors regardless of
// grade.
const PERCENT_GRADES = new Set(["Grade 7", "Grade 8"]);
const usesPercentage = (grade, reportType) => {
  if (!grade) return false;
  if (reportType === "progress_report") return false;
  return PERCENT_GRADES.has(grade);
};
const usesProgressDescriptors = (reportType) =>
  reportType === "progress_report";

// Ontario Growing Success report-card character ceilings.
// SSHG split (Jun 2026) — these limits now come from the backend
// (`/report-cards/builder-data` → `subject_char_limits` +
// `learning_skills_char_limit`) so there is ONE source of truth and the
// frontend no longer carries a divergent copy. The constants below are
// fallbacks only, used for the brief window before the first fetch
// resolves (or if the field is ever absent).
const DEFAULT_SUBJECT_CHAR_LIMIT = 1200;
const DEFAULT_LEARNING_SKILLS_CHAR_LIMIT = 2600;

// Small inline counter rendered under each comment textarea. Mirrors the
// Phase 17 paste-limit pattern: stays muted slate while under, switches
// to warm amber + bold when the teacher exceeds the board cap (we don't
// hard-block — teachers can keep typing and trim afterwards).
const CommentCharCounter = ({ value, limit, testId }) => {
  const used = (value || "").length;
  const over = used > limit;
  return (
    <div
      data-testid={testId}
      className={`mt-1 text-[11px] font-extrabold tabular-nums tracking-wide ${
        over ? "text-[#c2410c]" : "text-[#4a6984]/65"
      }`}
    >
      {used.toLocaleString()} / {limit.toLocaleString()} characters
      {over && (
        <span className="ml-2 font-semibold normal-case tracking-normal">
          · over by {(used - limit).toLocaleString()}
        </span>
      )}
    </div>
  );
};

// Storage key for autosave — scoped per teacher (handled by browser per-origin already).
const storageKey = (subject, period) =>
  `atomicedu.reportcards.${period || "progress"}.${subject}`;

/**
 * Per-subject undo/redo manager — a small custom history stack that
 * tracks the comment text. We push a snapshot every time the value
 * "stabilises" (user pauses typing, generation completes, or Clear is
 * clicked). Ctrl+Z and Ctrl+Y operate on that stack.
 */
const useCommentHistory = (initial = "") => {
  // Stack of snapshots. `index` is where we currently are.
  const [stack, setStack] = useState([initial]);
  const [index, setIndex] = useState(0);
  const lastSnapshotRef = useRef(initial);

  const value = stack[index] ?? "";

  const pushSnapshot = useCallback((next) => {
    if (next === lastSnapshotRef.current) return;
    lastSnapshotRef.current = next;
    setStack((prev) => {
      const trimmed = prev.slice(0, index + 1);
      // Cap at 50 entries so we don't grow forever.
      const updated = [...trimmed, next].slice(-50);
      setIndex(updated.length - 1);
      return updated;
    });
  }, [index]);

  const setValue = useCallback((next) => {
    // Light-touch update — we don't push a snapshot on every keystroke,
    // we wait for blur / pause via pushSnapshot. The textarea binds to
    // this so typing is instant.
    setStack((prev) => {
      const updated = [...prev];
      updated[index] = next;
      return updated;
    });
  }, [index]);

  const undo = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  const redo = useCallback(() => {
    setIndex((i) => Math.min(stack.length - 1, i + 1));
  }, [stack.length]);

  const canUndo = index > 0;
  const canRedo = index < stack.length - 1;

  const reset = useCallback((next) => {
    lastSnapshotRef.current = next;
    setStack([next]);
    setIndex(0);
  }, []);

  return { value, setValue, pushSnapshot, undo, redo, canUndo, canRedo, reset };
};

/**
 * CoveragePill — renders the COVERED + TAUGHT chips for an expectation
 * row and gates a popover that lists the source activities, when they
 * were taught, and an inline "Mark as not taught" undo. Phase 108
 * Live Reporting two-state model:
 *   • COVERED = auto-derived from the activities the teacher generated
 *     (curriculum links + body-mined codes, curriculum-validated).
 *   • TAUGHT  = subset that the teacher has explicitly confirmed (or
 *     that Math Engine auto-confirmed by walking the exit-ticket loop).
 *   Only TAUGHT expectations feed Live Reports comment generation.
 */
const CoveragePill = ({ row, activityMeta, onActivityChange }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const taughtIds = row.taught_activity_ids || [];
  const allIds = row.activity_ids || [];
  const isTaught = !!row.taught;
  // Item 1 status-level Undo target. TAUGHT chips revert to covered-only
  // (un-taught every source activity). COVERED-only chips dismiss the
  // derived coverage entirely (persisted server-side).
  const undoLabel = isTaught ? "Taught" : "Covered";

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return null;
    }
  };

  const runUndo = async () => {
    setBusy(true);
    try {
      if (isTaught) {
        // Un-taught every source activity for this expectation. Cascade=false
        // so we never touch unrelated Math Engine siblings.
        await Promise.all(taughtIds.map((aid) => unmarkActivityTaught(aid)));
        toast.success(`Marked ${row.code} as not taught`);
      } else {
        await dismissCoverage(row.code);
        toast.success(`Dismissed coverage for ${row.code}`);
      }
      setConfirmOpen(false);
      setOpen(false);
      if (onActivityChange) onActivityChange();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Update failed";
      toast.error("Couldn't undo", { description: String(detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`rc-exp-coverage-${row.code}`}
            className="ml-2 inline-flex items-center gap-1 align-middle hover:opacity-80 transition cursor-pointer"
            title="Tap to see source activity"
          >
            <span
              data-testid={`rc-exp-covered-${row.code}`}
              className={`ae-pill !py-0 !px-1.5 !text-[9px] !font-extrabold uppercase tracking-[0.1em] inline-flex items-center gap-1 ${
                isTaught
                  ? "bg-white text-[#4a6984]/70 border border-[#4a6984]/20"
                  : "bg-[#96ded1]/45 text-[#1c2d3d] border border-[#96ded1]"
              }`}
            >
              <CheckCircle2 className="h-2.5 w-2.5" /> Covered
            </span>
            {isTaught ? (
              <span
                data-testid={`rc-exp-taught-${row.code}`}
                className="ae-pill !py-0 !px-1.5 !text-[9px] !font-extrabold uppercase tracking-[0.1em] inline-flex items-center gap-1 bg-[#1c9e7e] text-white border border-[#1c9e7e]"
                title="You've confirmed teaching at least one activity for this expectation. Eligible for Live Reports."
              >
                <Sparkles className="h-2.5 w-2.5" /> Taught
              </span>
            ) : null}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          data-testid={`rc-exp-popover-${row.code}`}
          // max-w-[90vw] keeps the popover from forcing horizontal scroll on phones.
          className="w-80 max-w-[calc(100vw-2rem)] p-3 bg-white border border-[#4a6984]/15 shadow-lg"
        >
          <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#4a6984]/70">
            {row.code} · {isTaught ? "Covered + Taught" : "Covered"}
          </p>
          <p className="text-[11px] text-[#4a6984]/85 mt-1 mb-2 leading-relaxed">
            {isTaught
              ? `Confirmed taught via ${taughtIds.length} of ${allIds.length} source ${allIds.length === 1 ? "activity" : "activities"}.`
              : `Auto-covered by ${allIds.length} source ${allIds.length === 1 ? "activity" : "activities"} — none marked taught yet.`}
          </p>
          <ul className="space-y-1.5 max-h-60 overflow-y-auto">
            {allIds.map((aid) => {
              const meta = activityMeta[aid] || {};
              const isAidTaught = !!meta.taught;
              const date = formatDate(meta.taught_at);
              const name = meta.activity_name || "Untitled activity";
              return (
                <li
                  key={aid}
                  data-testid={`rc-exp-source-${row.code}-${aid}`}
                  className="rounded-md border border-[#4a6984]/10 px-2 py-1.5 bg-[#f4f5f7]/50"
                >
                  <a
                    href={`/lesson/${aid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`rc-exp-source-link-${row.code}-${aid}`}
                    className="text-xs font-bold text-[#1c2d3d] leading-tight inline-flex items-start gap-1 hover:text-[#1c9e7e] transition-colors"
                  >
                    <FileText className="h-3 w-3 mt-0.5 shrink-0" />
                    <span className="underline-offset-2 hover:underline">{name}</span>
                    <ExternalLink className="h-2.5 w-2.5 mt-0.5 shrink-0 opacity-60" />
                  </a>
                  <p className="text-[10px] text-[#4a6984]/75 mt-1">
                    {isAidTaught ? `Taught${date ? ` · ${date}` : ""}` : "Not yet marked taught"}
                  </p>
                </li>
              );
            })}
          </ul>
          {/* Item 1 — single chip-level Undo. Triggers a yes/no confirm before
              mutating state so accidental taps can't lose a coverage mark. */}
          <div className="mt-3 pt-2 border-t border-[#4a6984]/10 flex items-center justify-between gap-2">
            <span className="text-[10px] text-[#4a6984]/60 font-medium">
              View only — undo asks first.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              data-testid={`rc-exp-undo-${row.code}`}
              className="h-7 px-2.5 text-[10px] font-extrabold uppercase tracking-[0.1em] border-[#b45309]/40 text-[#b45309] hover:bg-[#b45309]/10 hover:border-[#b45309]"
            >
              <Undo2 className="h-3 w-3 mr-1" /> Undo {undoLabel}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <AlertDialog open={confirmOpen} onOpenChange={(v) => !busy && setConfirmOpen(v)}>
        <AlertDialogContent
          data-testid={`rc-exp-undo-confirm-${row.code}`}
          className="bg-white border border-[#4a6984]/15 max-w-[calc(100vw-2rem)] sm:max-w-md"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#4a6984] font-black">
              Undo this and remove {undoLabel} status for {row.code}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a6984]/80 text-sm leading-relaxed">
              {isTaught
                ? `This unmarks every activity (${taughtIds.length}) that confirmed teaching for ${row.code}. The expectation will still show as Covered if any source activity remains.`
                : `This dismisses the auto-coverage match for ${row.code}. The expectation will no longer count toward your coverage stats or Live Reports. You can undo this dismissal by re-running the lesson or re-marking it.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid={`rc-exp-undo-cancel-${row.code}`}
              disabled={busy}
              className="border-[#4a6984]/25 text-[#4a6984] hover:bg-[#f4f5f7]"
            >
              No
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid={`rc-exp-undo-confirm-yes-${row.code}`}
              onClick={(e) => {
                e.preventDefault();
                runUndo();
              }}
              disabled={busy}
              className="bg-[#b45309] hover:bg-[#8a3d05] text-white font-bold"
            >
              {busy ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                  Undoing…
                </>
              ) : (
                "Yes"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};


const SubjectCard = ({
  subject,
  grade,
  reportingPeriod,
  reportType,
  defaultLevel = "",
  hideLevelPicker = false,
  defaultOpen = false,
  onCommentChange,
  activityMeta = {},
  onActivityChange,
  charLimit = DEFAULT_SUBJECT_CHAR_LIMIT,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  // Tick state for each kind. Maps id → level/percentage.
  // ids:
  //   expectation: code (e.g. "B1.3")
  //   learning_goal: text
  //   transferable_skill: name
  const [selExpectations, setSelExpectations] = useState({});
  const [selGoals, setSelGoals] = useState({});
  const [selSkills, setSelSkills] = useState({});
  // Freehand teacher observations about THIS student in THIS subject —
  // the AI uses them as a qualitative modifier so the comment lands as
  // specific to this child, not boilerplate.
  const [studentNotes, setStudentNotes] = useState("");

  const isPercent = usesPercentage(grade, reportType);
  const isProgressDescriptor = usesProgressDescriptors(reportType);
  // Pick which option list drives the level dropdowns for THIS report
  // type: PWD/PW/PVW on the Progress Report; letter grades on Term 1/2.
  const expectationLevelOptions = isProgressDescriptor
    ? PROGRESS_DESCRIPTORS
    : LEVEL_AB_OPTIONS;

  const history = useCommentHistory("");
  const [generating, setGenerating] = useState(false);
  const textareaRef = useRef(null);
  const snapshotTimerRef = useRef(null);

  // Load from storage on mount + when period changes
  useEffect(() => {
    const raw = localStorage.getItem(storageKey(subject.subject, reportingPeriod));
    history.reset(raw || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject.subject, reportingPeriod]);

  // Autosave (debounced)
  useEffect(() => {
    const id = setTimeout(() => {
      localStorage.setItem(storageKey(subject.subject, reportingPeriod), history.value || "");
    }, 600);
    return () => clearTimeout(id);
  }, [history.value, subject.subject, reportingPeriod]);

  // Notify parent (tab completion indicator) whenever the comment changes.
  useEffect(() => {
    if (onCommentChange) onCommentChange(history.value);
  }, [history.value, onCommentChange]);

  // Phase 108.F — Live Reports "Send to subject tab" handoff. The Live
  // Reports panel dispatches `ae:reportcard:append` when the teacher
  // clicks "Send to [subject] tab". We append the chosen comment to
  // whatever's already in the draft (separator-aware), snapshot it for
  // undo, and focus the textarea so the teacher can immediately edit.
  useEffect(() => {
    const handler = (ev) => {
      const d = ev.detail || {};
      if (d.subject !== subject.subject) return;
      if (d.period && d.period !== reportingPeriod) return;
      const incoming = (d.text || "").trim();
      if (!incoming) return;
      const cur = (history.value || "").trim();
      const joined = cur ? `${cur}\n\n${incoming}` : incoming;
      history.setValue(joined);
      history.pushSnapshot(joined);
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(joined.length, joined.length);
        }
      }, 50);
    };
    window.addEventListener("ae:reportcard:append", handler);
    return () => window.removeEventListener("ae:reportcard:append", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject.subject, reportingPeriod, history.value]);

  // Snapshot when user pauses typing
  const handleChange = (e) => {
    history.setValue(e.target.value);
    if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current);
    snapshotTimerRef.current = setTimeout(() => history.pushSnapshot(e.target.value), 800);
  };

  // Keyboard shortcuts
  const handleKeyDown = (e) => {
    const mod = e.ctrlKey || e.metaKey;
    if (!mod) return;
    const k = e.key.toLowerCase();
    if (k === "z" && !e.shiftKey) {
      e.preventDefault();
      history.undo();
    } else if ((k === "y") || (k === "z" && e.shiftKey)) {
      e.preventDefault();
      history.redo();
    }
  };

  const toggleExpectation = (code) => {
    setSelExpectations((prev) => {
      const next = { ...prev };
      if (code in next) delete next[code];
      else next[code] = defaultLevel;
      return next;
    });
  };

  const toggleGoal = (text) => {
    setSelGoals((prev) => {
      const next = { ...prev };
      if (text in next) delete next[text];
      else next[text] = defaultLevel;
      return next;
    });
  };

  const toggleSkill = (name) => {
    setSelSkills((prev) => {
      const next = { ...prev };
      if (name in next) delete next[name];
      else next[name] = defaultLevel;
      return next;
    });
  };

  const collectItems = () => {
    const items = [];
    for (const [code, lvl] of Object.entries(selExpectations)) {
      const row = subject.expectations.find((r) => r.code === code);
      if (!row || !lvl) continue;
      items.push({
        kind: "expectation",
        code: row.code,
        description: row.description,
        ...(isPercent ? { percentage: Number(lvl) } : { level: lvl }),
      });
    }
    for (const [text, lvl] of Object.entries(selGoals)) {
      if (!lvl) continue;
      items.push({
        kind: "learning_goal",
        text,
        ...(isPercent ? { percentage: Number(lvl) } : { level: lvl }),
      });
    }
    for (const [name, lvl] of Object.entries(selSkills)) {
      if (!lvl) continue;
      items.push({ kind: "transferable_skill", name, level: lvl });
    }
    return items;
  };

  // Phase 113.A — LRP context auto-refresh. Re-pull whenever the
  // teacher switches subject tabs OR changes report type. Fetch runs
  // in the background; the panel renders an empty state when nothing
  // matches (or hides entirely if the teacher has no LRP doc yet).
  const [lrpUnits, setLrpUnits] = useState([]);
  const [hasLrp, setHasLrp] = useState(true); // optimistic — hides until proven absent
  const [lrpOpen, setLrpOpen] = useState(true);
  const [lrpLoading, setLrpLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!reportType || !subject?.subject) return undefined;
    setLrpLoading(true);
    getRcbLrpContext(subject.subject, reportType)
      .then((res) => {
        if (cancelled) return;
        setLrpUnits(Array.isArray(res?.units) ? res.units : []);
        setHasLrp(Boolean(res?.has_lrp));
      })
      .catch(() => {
        // 403 (non-Pro) or transient errors — silently no-op so the
        // panel just disappears rather than nag the teacher.
        if (cancelled) return;
        setLrpUnits([]);
        setHasLrp(false);
      })
      .finally(() => {
        if (!cancelled) setLrpLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [subject?.subject, reportType]);

  // Phase 113.C — Recently-taught expectations. Surface the 2-3 most
  // recently confirmed-taught codes at the TOP of the expectation list
  // with a faint "RECENTLY TAUGHT" badge. Strictly observational —
  // platform surfaces, teacher judges. Re-pulls on subject tab switch.
  const [recentlyTaughtCodes, setRecentlyTaughtCodes] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!subject?.subject) return undefined;
    getRcbRecentlyTaughtExpectations(subject.subject, 3)
      .then((res) => {
        if (cancelled) return;
        setRecentlyTaughtCodes(Array.isArray(res?.codes) ? res.codes : []);
      })
      .catch(() => {
        if (cancelled) return;
        setRecentlyTaughtCodes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [subject?.subject]);
  const recentSet = useMemo(
    () => new Set(recentlyTaughtCodes.map((c) => (c || "").toUpperCase())),
    [recentlyTaughtCodes]
  );

  const handleGenerate = async (mode /* "replace" | "append" */) => {
    if (!grade) {
      toast.error("Pick the student's grade first (top of the page).");
      return;
    }
    const items = collectItems();
    if (!items.length) {
      toast.warning("Tick at least one item and assign a level first.");
      return;
    }
    setGenerating(true);
    const t = toast.loading("Drafting comment…");
    try {
      const { comment } = await generateReportCardBuilderComment({
        subject: subject.subject,
        grade,
        reporting_period: reportingPeriod,
        report_type: reportType,
        items,
        existing_comment: mode === "append" ? history.value : "",
        student_notes: studentNotes,
      });
      toast.dismiss(t);
      const next = mode === "append" && history.value.trim()
        ? `${history.value.trim()} ${comment.trim()}`
        : comment.trim();
      history.pushSnapshot(next);
      toast.success(mode === "append" ? "Comment added below" : "Draft ready");
      // After successful generation, clear ONLY the items so the next round starts fresh.
      setSelExpectations({});
      setSelGoals({});
      setSelSkills({});
    } catch (err) {
      toast.dismiss(t);
      const detail = err?.response?.data?.detail || err?.message || "Generation failed";
      toast.error("Couldn't draft the comment", { description: String(detail) });
    } finally {
      setGenerating(false);
    }
  };

  const handleClear = () => {
    if (!history.value) return;
    if (!window.confirm(`Clear the ${subject.subject} comment? You can still Undo afterward.`)) return;
    history.pushSnapshot("");
  };

  const handleCopy = async () => {
    if (!history.value) return;
    try {
      await navigator.clipboard.writeText(history.value);
      toast.success(`${subject.subject} comment copied`);
    } catch (e) {
      toast.error("Couldn't copy — try selecting the text manually");
    }
  };

  const totalSelected =
    Object.keys(selExpectations).length +
    Object.keys(selGoals).length +
    Object.keys(selSkills).length;

  const renderLevelField = (value, onChange, options, testid) => (
    // Mobile tap-target fix: trigger goes full-width and 44px tall on
    // phones (Apple HIG minimum) while keeping the compact 36px / 176px
    // shape on tablet+ to avoid bloating the row layout.
    <Select value={value || ""} onValueChange={(v) => onChange(v === "__clear__" ? "" : v)}>
      <SelectTrigger
        data-testid={testid}
        className="h-11 sm:h-9 w-full sm:w-44 bg-white border border-[#4a6984]/20 text-[#4a6984] text-xs font-semibold"
      >
        <SelectValue placeholder="Level…" />
      </SelectTrigger>
      <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
        {options.map((o) => (
          <SelectItem
            key={o.value}
            value={o.value}
            className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 sm:py-2 text-sm sm:text-xs"
          >
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const renderPercentField = (value, onChange, testid) => (
    <Input
      type="number"
      min={0}
      max={100}
      step={1}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder="0-100"
      data-testid={testid}
      className="h-11 sm:h-9 w-full sm:w-24 bg-white border border-[#4a6984]/20 text-[#4a6984] text-xs font-semibold text-center"
    />
  );

  const expCount = subject.expectations.length;
  const goalCount = subject.learning_goals.length;
  const skillCount = subject.transferable_skills.length;
  const coveredExpCount = subject.expectations.filter((e) => e.covered).length;
  const coveredCount =
    coveredExpCount +
    subject.transferable_skills.filter((s) => s.covered).length;
  // Phase 108 — TAUGHT subset (teacher-confirmed). Always ≤ coveredCount.
  const taughtCount = subject.expectations.filter((e) => e.taught).length;

  return (
    <div
      data-testid={`rc-subject-${subject.subject.replace(/[^\w]+/g, "-")}`}
      className="ae-card rounded-xl border border-[#4a6984]/15 bg-white shadow-sm overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-[#f4f5f7]/60"
      >
        <div className="flex items-center gap-3 min-w-0">
          {open ? (
            <ChevronDown className="h-4 w-4 text-[#4a6984]/70 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-[#4a6984]/70 shrink-0" />
          )}
          <h3 className="font-extrabold text-base text-[#4a6984] tracking-tight">
            {subject.subject}
          </h3>
          <span className="ae-pill !py-0.5 !px-2 !text-[10px] bg-[#f4f5f7] text-[#4a6984]/80">
            {expCount} expectation{expCount === 1 ? "" : "s"} ·{" "}
            {coveredCount} covered ·{" "}
            <span className="text-[#1c9e7e] font-extrabold" data-testid={`rc-subject-taught-count-${subject.subject.replace(/[^\w]+/g, "-")}`}>
              {taughtCount} taught
            </span>{" "}
            ·{" "}
            {goalCount} learning goal{goalCount === 1 ? "" : "s"} ·{" "}
            {skillCount} skill{skillCount === 1 ? "" : "s"}
          </span>
        </div>
        {totalSelected > 0 ? (
          <span className="ae-pill !py-0.5 !px-2 !text-[10px] bg-[#96ded1]/35 text-[#1c2d3d] font-extrabold">
            {totalSelected} selected
          </span>
        ) : null}
      </button>

      {open && (
        <div className="px-5 pb-6 pt-2 space-y-6 border-t border-[#4a6984]/10">
          {/* Phase 113.A — Long Range Plan context panel. Read-only.
              Auto-refreshes when the teacher switches subject tabs (the
              useEffect above re-fires on subject.subject change). The
              panel serves both the AI generation path AND manual
              comment writing — units, expectations, and transferable
              skill focus stay one glance away from the textarea. */}
          {hasLrp ? (
            <section
              data-testid={`rc-lrp-panel-${subject.subject.replace(/[^\w]+/g, "-")}`}
              className="rounded-lg border border-[#96ded1]/55 bg-[#96ded1]/10 overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setLrpOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-[#96ded1]/15 transition-colors"
                data-testid={`rc-lrp-toggle-${subject.subject.replace(/[^\w]+/g, "-")}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {lrpOpen ? (
                    <ChevronDown className="h-3.5 w-3.5 text-[#1c9e7e] shrink-0" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-[#1c9e7e] shrink-0" />
                  )}
                  <BookOpen className="h-3.5 w-3.5 text-[#1c9e7e] shrink-0" />
                  <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#1c2d3d]">
                    Units from your Long Range Plan
                  </span>
                  <span className="text-[10px] font-semibold text-[#4a6984]/70 truncate">
                    · {REPORT_TYPE_LABEL[reportType] || reportType} · {subject.subject}
                  </span>
                </div>
                <span
                  className="ae-pill !py-0.5 !px-2 !text-[10px] bg-white text-[#1c2d3d] border border-[#96ded1]"
                  data-testid={`rc-lrp-count-${subject.subject.replace(/[^\w]+/g, "-")}`}
                >
                  {lrpLoading ? "…" : `${lrpUnits.length} unit${lrpUnits.length === 1 ? "" : "s"}`}
                </span>
              </button>
              {lrpOpen ? (
                <div className="px-4 pb-4 pt-1 border-t border-[#96ded1]/35 bg-white/50">
                  {lrpLoading ? (
                    <p className="text-[11px] text-[#4a6984]/65 italic">Loading your plan…</p>
                  ) : lrpUnits.length === 0 ? (
                    <div className="py-2 space-y-1.5">
                      <p className="text-[12px] text-[#4a6984]/85 leading-snug">
                        No Long Range Plan units found for this period. Add units to your Long Range Plan to have them surface here.
                      </p>
                      <a
                        href="/long-range-plan"
                        data-testid={`rc-lrp-empty-link-${subject.subject.replace(/[^\w]+/g, "-")}`}
                        className="inline-flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#1c9e7e] hover:text-[#177a61]"
                      >
                        Open Long Range Plan
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  ) : (
                    <>
                      <ul className="flex flex-wrap gap-1.5 py-2">
                        {lrpUnits.map((u, i) => {
                          const meta = [];
                          if (u.strand) meta.push(u.strand);
                          if (u.transferable_skills?.length)
                            meta.push(u.transferable_skills.slice(0, 2).join(" · "));
                          return (
                            <li
                              key={`${u.unit_activity_id || u.unit_name}-${i}`}
                              data-testid={`rc-lrp-chip-${subject.subject.replace(/[^\w]+/g, "-")}-${i}`}
                              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#96ded1] px-2.5 py-1 text-[11px] text-[#1c2d3d] shadow-sm"
                            >
                              <span className="h-1.5 w-1.5 rounded-full bg-[#1c9e7e] shrink-0" aria-hidden />
                              <span className="font-extrabold truncate max-w-[260px]" title={u.unit_name}>
                                {u.unit_name || "Untitled unit"}
                              </span>
                              {meta.length ? (
                                <span className="text-[#4a6984]/70 font-medium truncate max-w-[220px]">
                                  · {meta.join(" · ")}
                                </span>
                              ) : null}
                              {u.expectations?.length ? (
                                <span
                                  className="ae-pill !py-0 !px-1.5 !text-[9px] bg-[#f4f5f7] text-[#4a6984] border border-[#4a6984]/15"
                                  title={u.expectations.map((e) => `${e.code} ${e.description || ""}`).join("\n")}
                                >
                                  {u.expectations.length} exp
                                </span>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                      <p className="text-[10px] text-[#4a6984]/60 leading-snug">
                        Read-only context. The AI uses these as grounding for what you taught this period; it won&apos;t mention unit names verbatim.
                      </p>
                    </>
                  )}
                </div>
              ) : null}
            </section>
          ) : null}

          {/* Curriculum Expectations */}
          <section>
            <h4 className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/70 mb-2">
              Curriculum expectations{" "}
              {grade ? (
                <span className="text-[#4a6984]/55 normal-case tracking-normal font-medium">
                  · {grade}
                </span>
              ) : null}
            </h4>
            {grade && expCount > 0 && coveredExpCount === 0 ? (
              <div
                data-testid={`rc-honest-zero-${subject.subject.replace(/[^\w]+/g, "-")}`}
                className="mb-3 rounded-md border border-[#4a6984]/20 bg-[#f4f5f7] px-3 py-2.5 text-[12px] text-[#4a6984]/85 leading-relaxed"
              >
                Nothing marked taught yet. Coverage counts the expectations you&apos;ve marked taught — your selections saved fine; build a lesson or unit, teach it, then mark it taught in My Library. (The list below sets what a comment is about, not coverage.)
              </div>
            ) : null}
            {!grade ? (
              <p className="text-xs text-[#4a6984]/55 italic">
                Pick the student's grade above to load every Ontario expectation for this subject.
              </p>
            ) : expCount === 0 ? (
              <p className="text-xs text-[#4a6984]/55 italic">
                No expectations available for this (grade, subject) combination yet.
              </p>
            ) : (
              <ul className="space-y-2">
                {[...subject.expectations]
                  .sort((a, b) => {
                    // Phase 113.C — recently-taught codes float to the
                    // top, preserving the order returned by the API
                    // (most recent first). Within each band, original
                    // curriculum order is preserved.
                    const aRecent = recentSet.has((a.code || "").toUpperCase());
                    const bRecent = recentSet.has((b.code || "").toUpperCase());
                    if (aRecent && !bRecent) return -1;
                    if (!aRecent && bRecent) return 1;
                    if (aRecent && bRecent) {
                      const ai = recentlyTaughtCodes.indexOf(a.code.toUpperCase());
                      const bi = recentlyTaughtCodes.indexOf(b.code.toUpperCase());
                      return ai - bi;
                    }
                    return 0;
                  })
                  .map((row) => {
                  const checked = row.code in selExpectations;
                  const value = selExpectations[row.code] || "";
                  const isRecent = recentSet.has((row.code || "").toUpperCase());
                  return (
                    <li
                      key={row.code}
                      data-testid={`rc-exp-row-${row.code}`}
                      className={`flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 rounded-md border px-3 py-2 ${
                        isRecent
                          ? "border-[#96ded1] bg-[#96ded1]/12"
                          : "border-[#4a6984]/10 bg-[#f4f5f7]/40"
                      }`}
                    >
                      <label className="flex items-start gap-2.5 cursor-pointer flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleExpectation(row.code)}
                          data-testid={`rc-exp-${row.code}`}
                          className="mt-1 h-4 w-4 rounded border-[#4a6984]/40 accent-[#1c2d3d] shrink-0"
                        />
                        <span className="text-sm text-[#4a6984] leading-snug flex-1 min-w-0">
                          <span className="font-extrabold text-[#1c2d3d]">{row.code}</span>
                          {isRecent ? (
                            <span
                              data-testid={`rc-exp-recent-${row.code}`}
                              className="ml-2 inline-flex items-center gap-1 align-middle ae-pill !py-0 !px-1.5 !text-[9px] !font-extrabold bg-white text-[#1c9e7e] border border-[#96ded1] uppercase tracking-[0.12em]"
                              title="One of your most recently taught expectations for this subject"
                            >
                              Recently taught
                            </span>
                          ) : null}
                          {row.description ? (
                            <span className="text-[#4a6984]/85"> — {row.description}</span>
                          ) : null}
                          {row.covered ? (
                            <CoveragePill
                              row={row}
                              activityMeta={activityMeta}
                              onActivityChange={onActivityChange}
                            />
                          ) : null}
                        </span>
                      </label>
                      {checked && !hideLevelPicker ? (
                        isPercent ? (
                          renderPercentField(
                            value,
                            (v) => setSelExpectations((p) => ({ ...p, [row.code]: v })),
                            `rc-exp-pct-${row.code}`
                          )
                        ) : (
                          renderLevelField(
                            value,
                            (v) => setSelExpectations((p) => ({ ...p, [row.code]: v })),
                            expectationLevelOptions,
                            `rc-exp-level-${row.code}`
                          )
                        )
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Learning Goals */}
          <section>
            <h4 className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/70 mb-2">
              Learning goals covered
            </h4>
            {goalCount === 0 ? (
              <p className="text-xs text-[#4a6984]/55 italic">
                No learning goals captured yet — they're pulled from the WALT
                section of any generated lesson.
              </p>
            ) : (
              <ul className="space-y-2">
                {subject.learning_goals.map((row, i) => {
                  const checked = row.text in selGoals;
                  const value = selGoals[row.text] || "";
                  return (
                    <li
                      key={i}
                      className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 rounded-md border border-[#4a6984]/10 bg-[#f4f5f7]/40 px-3 py-2"
                    >
                      <label className="flex items-start gap-2.5 cursor-pointer flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleGoal(row.text)}
                          data-testid={`rc-goal-${i}`}
                          className="mt-1 h-4 w-4 rounded border-[#4a6984]/40 accent-[#1c2d3d] shrink-0"
                        />
                        <span className="text-sm text-[#4a6984] leading-snug">{row.text}</span>
                      </label>
                      {checked && !hideLevelPicker ? (
                        isPercent ? (
                          renderPercentField(
                            value,
                            (v) => setSelGoals((p) => ({ ...p, [row.text]: v })),
                            `rc-goal-pct-${i}`
                          )
                        ) : (
                          renderLevelField(
                            value,
                            (v) => setSelGoals((p) => ({ ...p, [row.text]: v })),
                            expectationLevelOptions,
                            `rc-goal-level-${i}`
                          )
                        )
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Transferable Skills */}
          <section>
            <h4 className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/70 mb-2">
              Transferable skills
            </h4>
            {skillCount === 0 ? (
              <p className="text-xs text-[#4a6984]/55 italic">
                No transferable skills available.
              </p>
            ) : (
              <ul className="space-y-2">
                {subject.transferable_skills.map((sk) => {
                  const name = sk.name;
                  const checked = name in selSkills;
                  const value = selSkills[name] || "";
                  const slug = name.replace(/[^\w]+/g, "-");
                  return (
                    <li
                      key={name}
                      className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 rounded-md border border-[#4a6984]/10 bg-[#f4f5f7]/40 px-3 py-2"
                    >
                      <label className="flex items-start gap-2.5 cursor-pointer flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSkill(name)}
                          data-testid={`rc-skill-${slug}`}
                          className="mt-1 h-4 w-4 rounded border-[#4a6984]/40 accent-[#1c2d3d] shrink-0"
                        />
                        <span className="text-sm text-[#4a6984] leading-snug font-semibold flex-1 min-w-0">
                          {name}
                          {sk.covered ? (
                            <span
                              data-testid={`rc-skill-covered-${slug}`}
                              className="ml-2 inline-flex items-center gap-1 align-middle ae-pill !py-0 !px-1.5 !text-[9px] !font-extrabold bg-[#96ded1]/45 text-[#1c2d3d] border border-[#96ded1] uppercase tracking-[0.1em]"
                              title="You've generated at least one activity targeting this transferable skill"
                            >
                              <CheckCircle2 className="h-2.5 w-2.5" /> Covered
                            </span>
                          ) : null}
                        </span>
                      </label>
                      {checked && !hideLevelPicker
                        ? renderLevelField(
                            value,
                            (v) => setSelSkills((p) => ({ ...p, [name]: v })),
                            LEARNING_SKILL_OPTIONS,
                            `rc-skill-level-${slug}`
                          )
                        : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Student-specific observations — drives a more personal comment */}
          <section className="pt-3 border-t border-[#4a6984]/10">
            <Label
              htmlFor={`rc-student-notes-${subject.subject.replace(/[^\w]+/g, "-")}`}
              className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/70 mb-1.5 block"
            >
              Notes about this student in {subject.subject} (Optional)
            </Label>
            <Textarea
              id={`rc-student-notes-${subject.subject.replace(/[^\w]+/g, "-")}`}
              value={studentNotes}
              onChange={(e) => setStudentNotes(e.target.value)}
              rows={3}
              data-testid={`rc-student-notes-${subject.subject.replace(/[^\w]+/g, "-")}`}
              placeholder="e.g. shows growing confidence with multi-step problems · contributed strong oral explanations during the fractions unit · still warming up to peer feedback in small groups"
              className="bg-white border border-[#4a6984]/20 text-[#4a6984] focus-visible:ring-2 focus-visible:ring-[#96ded1] rounded-md leading-relaxed text-sm"
            />
            <p className="mt-1 text-[10px] text-[#4a6984]/60 leading-snug">
              The composer weaves these observations in naturally — concrete classroom-real detail that makes the comment specific to this child, not boilerplate.
            </p>
          </section>

          {/* Comment workspace */}
          <section className="pt-2 border-t border-[#4a6984]/10">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <h4 className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/70">
                Comment for {subject.subject}
              </h4>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={history.undo}
                  disabled={!history.canUndo}
                  data-testid={`rc-undo-${subject.subject.replace(/[^\w]+/g, "-")}`}
                  className="h-8 px-2 text-[#4a6984] hover:bg-[#f4f5f7] disabled:opacity-30"
                  title="Undo (Ctrl/Cmd+Z)"
                >
                  <Undo2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={history.redo}
                  disabled={!history.canRedo}
                  data-testid={`rc-redo-${subject.subject.replace(/[^\w]+/g, "-")}`}
                  className="h-8 px-2 text-[#4a6984] hover:bg-[#f4f5f7] disabled:opacity-30"
                  title="Redo (Ctrl/Cmd+Y or Cmd+Shift+Z)"
                >
                  <Redo2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleClear}
                  data-testid={`rc-clear-${subject.subject.replace(/[^\w]+/g, "-")}`}
                  className="h-8 px-2 text-[#4a6984] hover:bg-[#f4f5f7]"
                  title="Clear comment"
                >
                  <Eraser className="h-3.5 w-3.5 mr-1" /> Clear
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleCopy}
                  data-testid={`rc-copy-${subject.subject.replace(/[^\w]+/g, "-")}`}
                  className="h-8 px-2 text-[#4a6984] hover:bg-[#f4f5f7]"
                  title="Copy to clipboard"
                >
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                </Button>
              </div>
            </div>
            <Textarea
              ref={textareaRef}
              data-testid={`rc-comment-${subject.subject.replace(/[^\w]+/g, "-")}`}
              value={history.value}
              onChange={handleChange}
              onBlur={() => history.pushSnapshot(history.value)}
              onKeyDown={handleKeyDown}
              rows={6}
              placeholder="Your comment will appear here. Tick items above, set a level, and click Generate. Edit freely — Ctrl/Cmd+Z undoes, Ctrl/Cmd+Y redoes."
              className="bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus-visible:ring-2 focus-visible:ring-[#96ded1] rounded-md leading-relaxed"
            />
            <CommentCharCounter
              value={history.value}
              limit={charLimit}
              testId={`rc-charcount-${subject.subject.replace(/[^\w]+/g, "-")}`}
            />
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-[11px] text-[#4a6984]/55">
                Placeholders <code>[Name]</code> and <code>[their, they]</code>{" "}
                stay in the draft — replace at the very end before exporting.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  onClick={() => handleGenerate("replace")}
                  disabled={generating || totalSelected === 0}
                  data-testid={`rc-generate-${subject.subject.replace(/[^\w]+/g, "-")}`}
                  className="ae-cta hover:ae-cta h-10 px-4 rounded-md font-semibold"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      Drafting…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate Comment
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleGenerate("append")}
                  disabled={generating || totalSelected === 0 || !history.value.trim()}
                  data-testid={`rc-append-${subject.subject.replace(/[^\w]+/g, "-")}`}
                  className="h-10 px-4 rounded-md font-semibold border-[#4a6984]/30 text-[#4a6984] hover:bg-[#f4f5f7]"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Add to Comment
                </Button>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

const LearningSkillsCard = ({ skills, grade, reportingPeriod, reportType, onCommentChange, charLimit = DEFAULT_LEARNING_SKILLS_CHAR_LIMIT }) => {
  const [open, setOpen] = useState(true);
  // For each ticked skill: { level: 'E'|'G'|'S'|'N'|'', lookFors: string[] }
  const [selected, setSelected] = useState({});
  // Which skill cards are currently expanded (showing their look-fors).
  const [expanded, setExpanded] = useState(() => new Set());
  // Freehand teacher observations about THIS student — drives a more
  // personal Learning Skills comment.
  const [studentNotes, setStudentNotes] = useState("");
  const history = useCommentHistory("");
  const [generating, setGenerating] = useState(false);
  const snapshotTimerRef = useRef(null);

  const subjectLabel = "Learning Skills & Work Habits";

  useEffect(() => {
    const raw = localStorage.getItem(storageKey(subjectLabel, reportingPeriod));
    history.reset(raw || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportingPeriod]);

  useEffect(() => {
    const id = setTimeout(() => {
      localStorage.setItem(storageKey(subjectLabel, reportingPeriod), history.value || "");
    }, 600);
    return () => clearTimeout(id);
  }, [history.value, reportingPeriod]);

  // Notify parent (tab completion indicator) whenever the comment changes.
  useEffect(() => {
    if (onCommentChange) onCommentChange(history.value);
  }, [history.value, onCommentChange]);

  const handleChange = (e) => {
    history.setValue(e.target.value);
    if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current);
    snapshotTimerRef.current = setTimeout(() => history.pushSnapshot(e.target.value), 800);
  };

  const handleKeyDown = (e) => {
    const mod = e.ctrlKey || e.metaKey;
    if (!mod) return;
    const k = e.key.toLowerCase();
    if (k === "z" && !e.shiftKey) {
      e.preventDefault();
      history.undo();
    } else if ((k === "y") || (k === "z" && e.shiftKey)) {
      e.preventDefault();
      history.redo();
    }
  };

  const toggleSkillExpansion = (name) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
    // Make sure the skill is selected when the teacher opens it — so
    // the look-fors checklist + level chooser have somewhere to write.
    setSelected((prev) => {
      if (name in prev) return prev;
      return { ...prev, [name]: { level: "", lookFors: [] } };
    });
  };

  const toggleSkillSelection = (name, e) => {
    // Called by the small ✕ "remove this skill" button. Doesn't toggle
    // the expansion — the expansion is purely a UI hint about which
    // look-fors panel is showing.
    if (e) e.stopPropagation();
    setSelected((prev) => {
      const next = { ...prev };
      if (name in next) delete next[name];
      else next[name] = { level: "", lookFors: [] };
      return next;
    });
    setExpanded((prev) => {
      if (!prev.has(name)) return prev;
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const setSkillLevel = (name, level) => {
    setSelected((prev) => ({
      ...prev,
      [name]: { level, lookFors: prev[name]?.lookFors || [] },
    }));
  };

  const toggleLookFor = (name, lookFor) => {
    setSelected((prev) => {
      const cur = prev[name] || { level: "", lookFors: [] };
      const has = cur.lookFors.includes(lookFor);
      return {
        ...prev,
        [name]: {
          ...cur,
          lookFors: has
            ? cur.lookFors.filter((l) => l !== lookFor)
            : [...cur.lookFors, lookFor],
        },
      };
    });
  };

  const items = Object.entries(selected)
    .filter(([, v]) => !!v?.level)
    .map(([name, v]) => ({
      kind: "learning_skill",
      name,
      level: v.level,
      look_fors_selected: v.lookFors || [],
    }));

  const handleGenerate = async (mode) => {
    if (!grade) {
      toast.error("Pick the student's grade first.");
      return;
    }
    if (items.length === 0) {
      toast.warning("Tick at least one learning skill and pick a level.");
      return;
    }
    setGenerating(true);
    const t = toast.loading("Drafting learning-skill comment…");
    try {
      const { comment } = await generateReportCardBuilderComment({
        subject: subjectLabel,
        grade,
        reporting_period: reportingPeriod,
        report_type: reportType,
        items,
        existing_comment: mode === "append" ? history.value : "",
        student_notes: studentNotes,
      });
      toast.dismiss(t);
      const next = mode === "append" && history.value.trim()
        ? `${history.value.trim()} ${comment.trim()}`
        : comment.trim();
      history.pushSnapshot(next);
      toast.success(mode === "append" ? "Comment added below" : "Draft ready");
      setSelected({});
      setExpanded(new Set());
    } catch (err) {
      toast.dismiss(t);
      const detail = err?.response?.data?.detail || err?.message || "Generation failed";
      toast.error("Couldn't draft the comment", { description: String(detail) });
    } finally {
      setGenerating(false);
    }
  };

  const handleClear = () => {
    if (!history.value) return;
    if (!window.confirm("Clear the Learning Skills comment? You can still Undo afterward.")) return;
    history.pushSnapshot("");
  };

  const handleCopy = async () => {
    if (!history.value) return;
    try {
      await navigator.clipboard.writeText(history.value);
      toast.success("Learning Skills comment copied");
    } catch (e) {
      toast.error("Couldn't copy — try selecting the text manually");
    }
  };

  // "selected" count = skills that have a chosen level. Skills that are
  // expanded for browsing but not yet level-assigned don't count.
  const total = items.length;

  return (
    <div
      data-testid="rc-learning-skills"
      className="ae-card rounded-xl border-2 border-[#96ded1]/60 bg-gradient-to-br from-[#96ded1]/10 to-white shadow-sm overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-[#f4f5f7]/40"
      >
        <div className="flex items-center gap-3 min-w-0">
          {open ? (
            <ChevronDown className="h-4 w-4 text-[#4a6984]/70 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-[#4a6984]/70 shrink-0" />
          )}
          <h3 className="font-extrabold text-base text-[#4a6984] tracking-tight">
            Learning Skills & Work Habits
          </h3>
          <span className="ae-pill !py-0.5 !px-2 !text-[10px] bg-[#96ded1]/30 text-[#1c2d3d] font-extrabold">
            Growing Success · E / G / S / N
          </span>
        </div>
        {total > 0 ? (
          <span className="ae-pill !py-0.5 !px-2 !text-[10px] bg-[#96ded1]/40 text-[#1c2d3d] font-extrabold">
            {total} selected
          </span>
        ) : null}
      </button>

      {open && (
        <div className="px-5 pb-6 pt-2 space-y-5 border-t border-[#96ded1]/30">
          <p className="text-xs text-[#4a6984]/75 leading-relaxed">
            These six Learning Skills appear once on every Ontario report card,
            graded separately from subject achievement. Click a skill to reveal
            its Growing Success look-fors, tick the ones that fit this student,
            assign E / G / S / N, then Generate.
          </p>
          <ul className="space-y-2">
            {skills.map((sk) => {
              const checked = sk.name in selected;
              const entry = selected[sk.name];
              const value = entry?.level || "";
              const isExpanded = expanded.has(sk.name);
              const lookForsForSkill = sk.look_fors || [];
              const lookForsSelected = entry?.lookFors || [];
              const slug = sk.name.replace(/[^\w]+/g, "-");
              return (
                <li
                  key={sk.name}
                  className={`rounded-md border bg-white ${
                    checked
                      ? "border-[#96ded1] shadow-sm"
                      : "border-[#4a6984]/10"
                  }`}
                  data-testid={`rc-ls-row-${slug}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2.5">
                    <button
                      type="button"
                      onClick={() => toggleSkillExpansion(sk.name)}
                      data-testid={`rc-ls-${slug}`}
                      aria-expanded={isExpanded}
                      className="flex items-center gap-2.5 text-left flex-1 min-w-0 group"
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-[#4a6984]/70 shrink-0 transition-transform" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-[#4a6984]/70 shrink-0 transition-transform" />
                      )}
                      <span className="text-sm text-[#4a6984] leading-snug font-semibold group-hover:text-[#1c2d3d]">
                        {sk.name}
                      </span>
                      {checked && lookForsSelected.length > 0 ? (
                        <span className="ae-pill !py-0.5 !px-2 !text-[10px] bg-[#96ded1]/30 text-[#1c2d3d] font-extrabold ml-1">
                          {lookForsSelected.length} look-for{lookForsSelected.length === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </button>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-1.5 w-full sm:w-auto">
                      {checked ? (
                        <Select
                          value={value || ""}
                          onValueChange={(v) =>
                            setSkillLevel(sk.name, v === "__clear__" ? "" : v)
                          }
                        >
                          <SelectTrigger
                            data-testid={`rc-ls-level-${slug}`}
                            className="h-11 sm:h-9 w-full sm:w-44 bg-white border border-[#4a6984]/20 text-[#4a6984] text-xs font-semibold"
                          >
                            <SelectValue placeholder="Level…" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
                            {LEARNING_SKILL_OPTIONS.map((o) => (
                              <SelectItem
                                key={o.value}
                                value={o.value}
                                className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 sm:py-2 text-sm sm:text-xs"
                              >
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : null}
                      {checked ? (
                        <button
                          type="button"
                          onClick={(e) => toggleSkillSelection(sk.name, e)}
                          data-testid={`rc-ls-remove-${slug}`}
                          aria-label={`Remove ${sk.name}`}
                          className="h-10 sm:h-8 self-start sm:self-auto px-3 sm:px-2 text-xs sm:text-[10px] font-bold uppercase tracking-[0.12em] text-[#4a6984]/60 hover:text-[#9b3a3a] hover:bg-[#f4f5f7] rounded"
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {isExpanded && (
                    <div
                      className="px-4 pb-3 pt-1 border-t border-[#4a6984]/10 bg-[#f4f5f7]/40"
                      data-testid={`rc-ls-lookfors-${slug}`}
                    >
                      <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/70 mb-2 mt-1">
                        Growing Success look-fors · tick what you've seen for this student
                      </p>
                      {lookForsForSkill.length === 0 ? (
                        <p className="text-xs text-[#4a6984]/55 italic">
                          (No look-fors loaded for this skill.)
                        </p>
                      ) : (
                        <ul className="space-y-1.5">
                          {lookForsForSkill.map((lf, idx) => {
                            const ticked = lookForsSelected.includes(lf);
                            return (
                              <li key={idx}>
                                <label
                                  className="flex items-start gap-2 cursor-pointer text-[12px] text-[#4a6984] leading-snug"
                                  data-testid={`rc-ls-lookfor-${slug}-${idx}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={ticked}
                                    onChange={() => toggleLookFor(sk.name, lf)}
                                    className="mt-0.5 h-3.5 w-3.5 rounded border-[#4a6984]/40 accent-[#1c2d3d] shrink-0"
                                  />
                                  <span className={ticked ? "text-[#1c2d3d] font-semibold" : ""}>
                                    {lf}
                                  </span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Student-specific observations — drives a more personal Learning Skills comment */}
          <section className="pt-3 border-t border-[#96ded1]/30">
            <Label
              htmlFor="rc-ls-student-notes"
              className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/70 mb-1.5 block"
            >
              Notes about this student's Learning Skills (Optional)
            </Label>
            <Textarea
              id="rc-ls-student-notes"
              value={studentNotes}
              onChange={(e) => setStudentNotes(e.target.value)}
              rows={3}
              data-testid="rc-ls-student-notes"
              placeholder="e.g. consistently sets up the classroom for partner work · sometimes interrupts in small group · stayed in at lunch to revise her science write-up · advocates for quieter classmates"
              className="bg-white border border-[#4a6984]/20 text-[#4a6984] focus-visible:ring-2 focus-visible:ring-[#96ded1] rounded-md leading-relaxed text-sm"
            />
            <p className="mt-1 text-[10px] text-[#4a6984]/60 leading-snug">
              The composer weaves these observations in naturally — concrete classroom-real detail that makes the comment specific to this child, not boilerplate.
            </p>
          </section>

          {/* Comment workspace */}
          <section className="pt-2 border-t border-[#96ded1]/30">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <h4 className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#4a6984]/70">
                Comment for Learning Skills & Work Habits
              </h4>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={history.undo}
                  disabled={!history.canUndo}
                  data-testid="rc-ls-undo"
                  className="h-8 px-2 text-[#4a6984] hover:bg-[#f4f5f7] disabled:opacity-30"
                  title="Undo (Ctrl/Cmd+Z)"
                >
                  <Undo2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={history.redo}
                  disabled={!history.canRedo}
                  data-testid="rc-ls-redo"
                  className="h-8 px-2 text-[#4a6984] hover:bg-[#f4f5f7] disabled:opacity-30"
                  title="Redo (Ctrl/Cmd+Y or Cmd+Shift+Z)"
                >
                  <Redo2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleClear}
                  data-testid="rc-ls-clear"
                  className="h-8 px-2 text-[#4a6984] hover:bg-[#f4f5f7]"
                  title="Clear comment"
                >
                  <Eraser className="h-3.5 w-3.5 mr-1" /> Clear
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleCopy}
                  data-testid="rc-ls-copy"
                  className="h-8 px-2 text-[#4a6984] hover:bg-[#f4f5f7]"
                  title="Copy to clipboard"
                >
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                </Button>
              </div>
            </div>
            <Textarea
              data-testid="rc-ls-comment"
              value={history.value}
              onChange={handleChange}
              onBlur={() => history.pushSnapshot(history.value)}
              onKeyDown={handleKeyDown}
              rows={6}
              placeholder="Your learning-skill comment will appear here. Tick skills above, set a level, and click Generate."
              className="bg-white border border-[#4a6984]/20 text-[#4a6984] font-medium focus-visible:ring-2 focus-visible:ring-[#96ded1] rounded-md leading-relaxed"
            />
            <CommentCharCounter
              value={history.value}
              limit={charLimit}
              testId="rc-ls-charcount"
            />
            <div className="mt-3 flex items-center justify-end gap-2 flex-wrap">
              <Button
                type="button"
                onClick={() => handleGenerate("replace")}
                disabled={generating || total === 0}
                data-testid="rc-ls-generate"
                className="ae-cta hover:ae-cta h-10 px-4 rounded-md font-semibold"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Drafting…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate Comment
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleGenerate("append")}
                disabled={generating || total === 0 || !history.value.trim()}
                data-testid="rc-ls-append"
                className="h-10 px-4 rounded-md font-semibold border-[#4a6984]/30 text-[#4a6984] hover:bg-[#f4f5f7]"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add to Comment
              </Button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

// Tab definitions for the report-card builder. Order matches the spec:
// Learning Skills first (default active), then subjects in Ontario report
// order. FSL only renders if the grade includes "Core French".
const REPORT_CARD_TABS = [
  { id: "learning_skills", label: "Learning Skills" },
  { id: "language", label: "Language", subject: "Language" },
  { id: "math", label: "Math", subject: "Mathematics" },
  { id: "science", label: "Science", subject: "Science and Technology" },
  // SSHG split (Jun 2026) — three distinct Ontario subjects as separate
  // tabs. The tab strip only renders a tab when the backend returns that
  // subject for the selected grade (`subjectByName[t.subject]`), so a
  // Grade 4-6 teacher sees only Social Studies and a Grade 7-8 teacher
  // sees only History + Geography — never all three at once.
  { id: "social_studies", label: "Social Studies", subject: "Social Studies" },
  { id: "history", label: "History", subject: "History" },
  { id: "geography", label: "Geography", subject: "Geography" },
  { id: "arts", label: "The Arts", subject: "The Arts" },
  {
    id: "hpe",
    label: "Health & Physical Education",
    subject: "Health and Physical Education",
  },
  {
    id: "fsl",
    label: "French as a Second Language",
    subject: "Core French",
  },
  // Phase 108.D / Item 2 (Feb 2026) — Live Reports is a special "tab"
  // that is NOT shown in the horizontal subject strip. It's reached via
  // a primary button beside Reporting Period and a persistent FAB once
  // any expectation is marked taught. Keeping the tab id intact lets the
  // existing render-path stay simple.
  { id: "live_reports", label: "Live Reports", hidden: true },
];

const ReportCardGenerator = () => {
  const { user } = useAuth();
  const canLiveReports = hasLiveReportComments(user);
  const [grade, setGrade] = useState("");
  // Phase 113.0 — the three real Ontario elementary reports. Stored
  // value is the snake_case key; user-facing label comes from
  // REPORT_TYPE_LABEL. `period` is the human-readable label sent
  // verbatim to the LLM (kept on the request body for the period-hint
  // map in the backend prompt).
  const [reportType, setReportType] = useState("progress_report");
  const period = REPORT_TYPE_LABEL[reportType] || "Progress Report";
  // Phase 113.B — Progress Report difficulty flags. Map of subject → bool.
  // On the Progress Report, non-core subjects (everything but Math + Lang)
  // are hidden from the tab strip until the teacher flips its PWD flag on.
  // Stored per-grade so a teacher who changes grade doesn't lose flags.
  const [pwdFlags, setPwdFlags] = useState({});
  const togglePwdFlag = useCallback((subjectName) => {
    setPwdFlags((prev) => {
      const next = { ...prev };
      if (next[subjectName]) delete next[subjectName];
      else next[subjectName] = true;
      return next;
    });
  }, []);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // SSHG fail-loud (Jun 2026) — when the backend cannot resolve the
  // student grade it returns an explicit error rather than silently
  // defaulting to Social Studies. We surface that as an inline message so
  // the teacher knows WHY nothing loaded, instead of a blank/partial
  // builder. Null = no error.
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    // No grade picked yet → don't call the API. This is the neutral
    // "pick a grade" pre-selection state, NOT an error: clear data and
    // let the empty-state card render.
    if (!grade) {
      setData(null);
      setLoadError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      // Phase 83c — honour the same Taught-only preference as the Tracker.
      let taughtOnly = false;
      try {
        taughtOnly = window.localStorage.getItem("ae_tracker_taught_only") === "1";
      } catch { /* localStorage may be unavailable */ }
      const res = await getReportCardBuilderData(grade, taughtOnly);
      setData(res);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || "Failed to load";
      // A 422 here means the grade could not be read/resolved (fail-loud
      // guard). Show the plain-language message inline rather than a
      // misleading empty builder.
      if (status === 422) {
        setData(null);
        setLoadError(
          "We couldn't read the grade for this report, so subjects can't be loaded. Please re-select the student grade above and try again.",
        );
      } else {
        toast.error("Couldn't load your covered curriculum", { description: String(detail) });
      }
    } finally {
      setLoading(false);
    }
  }, [grade]);

  useEffect(() => {
    load();
  }, [load]);

  const subjects = data?.subjects || [];
  const learningSkills = data?.learning_skills || [];
  // SSHG split (Jun 2026) — character ceilings come from the backend
  // response so the frontend has no divergent copy. Fallbacks apply only
  // before the first fetch resolves.
  const subjectCharLimits = data?.subject_char_limits || {};
  const learningSkillsCharLimit =
    data?.learning_skills_char_limit || DEFAULT_LEARNING_SKILLS_CHAR_LIMIT;
  // Phase 108 — Live Reporting. Activity meta lets each SubjectCard
  // render the source-activity popover (name + taught date + undo)
  // without an extra round trip per chip click.
  const activityMeta = data?.activity_meta || {};

  // Tabbed layout (Phase 90) — Learning Skills + one tab per subject.
  // We track each tab's current comment so we can flag "done" tabs with a
  // small checkmark. Children call `onCommentChange` whenever their comment
  // value changes (initial load from localStorage included).
  const [activeTab, setActiveTab] = useState("learning_skills");
  const [tabComments, setTabComments] = useState({});

  const handleCommentChange = useCallback((tabId) => (value) => {
    setTabComments((prev) => {
      if (prev[tabId] === value) return prev;
      return { ...prev, [tabId]: value };
    });
  }, []);

  const subjectByName = useMemo(() => {
    const m = {};
    subjects.forEach((s) => {
      m[s.subject] = s;
    });
    return m;
  }, [subjects]);

  // Only show a subject tab if the loaded data includes that subject (the
  // backend already filters subjects by grade). Learning Skills is always
  // visible. Live Reports is `hidden: true` and reached via the primary
  // button beside Reporting Period (and the FAB) — not the tab strip.
  // Phase 113.B — On the Progress Report, only Math + Language are
  // visible by default. Other subjects (Science, Social Studies, Arts,
  // HPE, FSL) only appear once the teacher has flagged them as
  // Progressing With Difficulty in the PWD-flag controls below.
  const isProgressReport = reportType === "progress_report";
  const visibleTabs = useMemo(
    () =>
      REPORT_CARD_TABS.filter((t) => {
        if (t.hidden) return false;
        if (t.id === "learning_skills") return true;
        if (!subjectByName[t.subject]) return false;
        if (isProgressReport) {
          if (PROGRESS_REPORT_DEFAULT_SUBJECTS.has(t.subject)) return true;
          return Boolean(pwdFlags[t.subject]);
        }
        return true;
      }),
    [subjectByName, isProgressReport, pwdFlags]
  );

  // Phase 113.B — The non-core subjects that COULD be PWD-flagged on a
  // Progress Report (filtered to those actually present for this grade).
  const pwdFlaggableSubjects = useMemo(
    () =>
      REPORT_CARD_TABS.filter(
        (t) =>
          t.subject &&
          !PROGRESS_REPORT_DEFAULT_SUBJECTS.has(t.subject) &&
          subjectByName[t.subject]
      ),
    [subjectByName]
  );

  // Any expectation marked TAUGHT anywhere in the loaded surface unlocks
  // the persistent FAB return-button. Cheap full scan — the curriculum
  // surface is at most a few hundred rows.
  const hasAnyTaught = useMemo(
    () =>
      subjects.some((s) =>
        (s.expectations || []).some((e) => e.taught)
      ),
    [subjects]
  );

  // If the previously-active tab disappears after a grade change (e.g. FSL
  // dropped), fall back to Learning Skills so the user isn't stranded on an
  // empty view. Live Reports is hidden from visibleTabs by design but is a
  // valid destination — preserve it here so openLiveReports() works.
  const effectiveTab =
    activeTab === "live_reports" || visibleTabs.find((t) => t.id === activeTab)
      ? activeTab
      : "learning_skills";

  const inLiveReports = effectiveTab === "live_reports";
  // Remember which subject tab the teacher was on so the back button
  // sends them right back, not to Learning Skills.
  const [previousTab, setPreviousTab] = useState("learning_skills");
  const openLiveReports = useCallback(() => {
    setPreviousTab((prev) =>
      activeTab === "live_reports" ? prev : activeTab
    );
    setActiveTab("live_reports");
  }, [activeTab]);
  const closeLiveReports = useCallback(() => {
    setActiveTab(previousTab || "learning_skills");
  }, [previousTab]);

  // Phase 108.F — "Send to subject tab" handoff. Find the tab id that
  // owns the given subject bucket, switch to it, then dispatch the
  // append event after the panel mounts. The SubjectCard listens for
  // `ae:reportcard:append` and appends the text to its draft (snapshotted
  // for undo). Toast surfaces success/skip reasons.
  const sendToSubject = useCallback(
    (subjectName, text) => {
      const tab = REPORT_CARD_TABS.find((t) => t.subject === subjectName);
      if (!tab) {
        toast.error(`No report card tab for "${subjectName}".`);
        return;
      }
      if (!subjectByName[tab.subject]) {
        toast.error(`The ${subjectName} tab isn't loaded — pick the matching grade first.`);
        return;
      }
      setActiveTab(tab.id);
      // Defer dispatch until the SubjectCard is the visible tabpanel
      // so its focus() can land on a rendered textarea.
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("ae:reportcard:append", {
            detail: { subject: subjectName, text, period },
          })
        );
        toast.success(`Sent to ${subjectName} draft.`);
      }, 80);
    },
    [period, subjectByName]
  );

  // The subset of subject buckets that currently have a builder tab
  // mounted — Live Reports limits its "Send to" button to these.
  const sendableSubjects = useMemo(
    () =>
      REPORT_CARD_TABS.filter((t) => t.subject && subjectByName[t.subject]).map(
        (t) => t.subject
      ),
    [subjectByName]
  );

  return (
    <div className="space-y-6" data-testid="report-card-builder">
      <header className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <p className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70 inline-flex items-center gap-2">
            <ScrollText className="h-3.5 w-3.5" /> Report Card Builder
          </p>
          <h2 className="mt-1.5 font-black text-2xl sm:text-3xl text-[#4a6984] tracking-tight">
            Build a report card comment from what you've actually taught.
          </h2>
          <p className="mt-2 text-[#4a6984]/80 font-medium max-w-2xl text-sm leading-relaxed">
            Every Ontario curriculum expectation for the student's grade is
            available below, grouped by subject. Items you've generated activities
            for are marked{" "}
            <span className="ae-pill !py-0 !px-1.5 !text-[9px] !font-extrabold bg-[#96ded1]/45 text-[#1c2d3d] border border-[#96ded1] uppercase tracking-[0.1em]">
              <CheckCircle2 className="h-2 w-2 inline -mt-0.5" /> Covered
            </span>
            {" "}— and items you&apos;ve confirmed teaching are also marked{" "}
            <span className="ae-pill !py-0 !px-1.5 !text-[9px] !font-extrabold bg-[#1c9e7e] text-white border border-[#1c9e7e] uppercase tracking-[0.1em]">
              <Sparkles className="h-2 w-2 inline -mt-0.5" /> Taught
            </span>
            . Tap a chip to see the source activity and undo if needed.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          onClick={load}
          disabled={loading}
          data-testid="rc-refresh"
          className="h-10 px-3 text-[#4a6984] hover:bg-[#f4f5f7]"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        <Button
          type="button"
          variant="ghost"
          asChild
          data-testid="rc-link-gradebook"
          className="h-10 px-3 text-[#4a6984] hover:bg-[#f4f5f7]"
        >
          <a href="/gradebook">
            <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" />
            Gradebook →
          </a>
        </Button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto] gap-4 max-w-4xl items-end">
        <div className="space-y-2">
          <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
            Student grade
          </Label>
          <Select value={grade || ""} onValueChange={setGrade}>
            <SelectTrigger data-testid="rc-grade" className={FIELD_CLASS}>
              <SelectValue placeholder="Select student grade" />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
              {GRADE_OPTIONS.map((g) => (
                <SelectItem
                  key={g}
                  value={g}
                  data-testid={`rc-grade-${g.replace(" ", "-").toLowerCase()}`}
                  className="text-[#4a6984] focus:bg-[#f4f5f7]"
                >
                  {g}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
            Report type
          </Label>
          <Select value={reportType} onValueChange={setReportType}>
            <SelectTrigger data-testid="rc-report-type" className={FIELD_CLASS}>
              <SelectValue placeholder="Select report type" />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
              {REPORT_TYPES.map((t) => (
                <SelectItem
                  key={t.value}
                  value={t.value}
                  data-testid={`rc-report-type-${t.value}`}
                  className="text-[#4a6984] focus:bg-[#f4f5f7]"
                >
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Item 2 — primary entry point. Mint button at the same visual
            weight as Start Building. Disabled until a grade is picked so
            the panel can filter subjects to the chosen grade. */}
        <div className="space-y-2 sm:col-span-2 lg:col-span-1">
          <Label className="text-transparent font-bold text-xs uppercase tracking-[0.15em] hidden lg:block select-none">
            &nbsp;
          </Label>
          {canLiveReports ? (
            <Button
              type="button"
              onClick={openLiveReports}
              disabled={!grade || inLiveReports}
              data-testid="rc-open-live-reports"
              className="h-11 px-5 w-full lg:w-auto bg-[#96ded1] hover:bg-[#7fcebf] text-[#1c2d3d] font-extrabold rounded-md border border-[#1c9e7e]/40 shadow-sm disabled:opacity-60"
            >
              <Wand2 className="h-4 w-4 mr-2" />
              Generate Live Report Comments
              <span aria-hidden="true" className="ml-1.5">→</span>
            </Button>
          ) : null}
        </div>
      </div>

      {loadError ? (
        <div
          className="ae-card p-6 border border-[#c2410c]/30 bg-[#fff7ed] text-center"
          data-testid="rc-grade-error"
        >
          <AlertTriangle className="h-6 w-6 text-[#c2410c] mx-auto mb-2" />
          <p className="text-[#7c2d12] font-bold">Couldn&apos;t load subjects</p>
          <p className="text-sm text-[#7c2d12]/80 mt-1 max-w-md mx-auto leading-relaxed">
            {loadError}
          </p>
        </div>
      ) : loading ? (
        <p className="text-[#4a6984]/70 italic" data-testid="rc-loading">Loading your covered curriculum…</p>
      ) : subjects.length === 0 ? (
        <div className="ae-card p-6 border border-dashed border-[#4a6984]/25 bg-white text-center" data-testid="rc-empty">
          <ClipboardCheck className="h-6 w-6 text-[#4a6984]/50 mx-auto mb-2" />
          <p className="text-[#4a6984] font-bold">No subjects yet</p>
          <p className="text-sm text-[#4a6984]/70 mt-1">
            Pick a student grade above to load every Ontario expectation, subject by subject.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Item 2 — when the teacher has opened Live Reports, hide the
              subject tab strip + per-subject panels and surface a return
              header. The Live Reports panel handles its own controls. */}
          {inLiveReports ? (
            <div
              role="tabpanel"
              data-testid="rc-tabpanel-live_reports"
              className="space-y-3"
            >
              <button
                type="button"
                onClick={closeLiveReports}
                data-testid="rc-back-to-builder"
                className="inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-[0.15em] text-[#4a6984] hover:text-[#1c9e7e] transition-colors"
              >
                <ChevronRight className="h-3.5 w-3.5 rotate-180" />
                Back to Report Card Builder
              </button>
              <LiveReportsPanel
                subjects={subjects}
                grade={grade}
                reportType={reportType}
                onSendToSubject={sendToSubject}
                sendableSubjects={sendableSubjects}
              />
            </div>
          ) : (
            <>
              {/* Phase 113.B — Progress Report difficulty flags. Only
                  surfaces on the Progress Report (Math + Language are
                  always shown; every other subject only appears once
                  flagged Progressing With Difficulty). */}
              {isProgressReport && pwdFlaggableSubjects.length > 0 ? (
                <div
                  data-testid="rc-pwd-flags"
                  className="rounded-lg border border-[#f59e0b]/35 bg-[#fff7ed] p-3"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-[#b45309]" />
                    <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#7c2d12]">
                      Progress Report — Flag subjects Progressing With Difficulty
                    </span>
                  </div>
                  <p className="text-[11px] text-[#7c2d12]/80 leading-snug mb-2.5">
                    Math &amp; Language always appear. Other subjects only generate a comment when you flag a student as Progressing With Difficulty in that subject — keep this focused on the areas that need a note.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {pwdFlaggableSubjects.map((t) => {
                      const flagged = Boolean(pwdFlags[t.subject]);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => togglePwdFlag(t.subject)}
                          data-testid={`rc-pwd-flag-${t.id}`}
                          aria-pressed={flagged}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.08em] border transition-colors ${
                            flagged
                              ? "bg-[#b45309] text-white border-[#b45309] hover:bg-[#8a3d05]"
                              : "bg-white text-[#7c2d12] border-[#f59e0b]/50 hover:bg-[#fef3c7]"
                          }`}
                        >
                          {flagged ? (
                            <CheckCircle2 className="h-3 w-3" />
                          ) : (
                            <Plus className="h-3 w-3" />
                          )}
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {/* Tab bar — horizontal scroll on mobile so all tabs stay on one
                  row instead of wrapping or collapsing into a dropdown. */}
              <div
                data-testid="rc-tab-bar"
                className="border-b border-[#4a6984]/15 -mx-4 sm:mx-0 px-4 sm:px-0"
              >
                <div
                  className="flex gap-1 overflow-x-auto -mb-px"
                  style={{ scrollbarWidth: "thin" }}
                >
                  {visibleTabs.map((t) => {
                    const done = (tabComments[t.id] || "").trim().length > 0;
                    const active = effectiveTab === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setActiveTab(t.id)}
                        data-testid={`rc-tab-${t.id}`}
                        aria-selected={active}
                        role="tab"
                        className={`whitespace-nowrap px-4 py-2.5 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 shrink-0 ${
                          active
                            ? "border-[#96ded1] text-[#1c2d3d]"
                            : "border-transparent text-[#4a6984]/70 hover:text-[#4a6984] hover:border-[#4a6984]/20"
                        }`}
                      >
                        {t.label}
                        {done ? (
                          <CheckCircle2
                            className="h-3.5 w-3.5 text-[#1c9e7e]"
                            data-testid={`rc-tab-done-${t.id}`}
                            aria-label="Comment drafted"
                          />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* All tab panels stay mounted (display:none for inactive ones)
                  so teachers can switch tabs without losing in-progress work —
                  selections, notes, and draft comments persist across tab
                  changes. */}
              <div
                role="tabpanel"
                data-testid="rc-tabpanel-learning_skills"
                className={effectiveTab === "learning_skills" ? "" : "hidden"}
              >
                <LearningSkillsCard
                  skills={learningSkills}
                  grade={grade}
                  reportingPeriod={period}
                  reportType={reportType}
                  onCommentChange={handleCommentChange("learning_skills")}
                  charLimit={learningSkillsCharLimit}
                />
              </div>
              {REPORT_CARD_TABS.filter((t) => t.subject).map((t) => {
                const subj = subjectByName[t.subject];
                if (!subj) return null;
                // On the Progress Report, gate non-core subject panels
                // behind the PWD flag toggle. Math & Language are always
                // open per the Ontario Progress Report spec.
                if (
                  isProgressReport &&
                  !PROGRESS_REPORT_DEFAULT_SUBJECTS.has(t.subject) &&
                  !pwdFlags[t.subject]
                ) {
                  return null;
                }
                // Phase 113.B — auto-default the level picker on the
                // Progress Report. PWD-flagged non-core subjects start
                // every ticked item at PWD; Math/Language start at PW
                // (most common observational descriptor). Saves the
                // teacher a click; still editable.
                let defaultLevel = "";
                let hideLevelPicker = false;
                if (isProgressReport) {
                  if (PROGRESS_REPORT_DEFAULT_SUBJECTS.has(t.subject)) {
                    defaultLevel = "PW";
                  } else {
                    // PWD-flagged non-core subject — the flag itself
                    // declares the descriptor; per-expectation pickers
                    // would be pure friction, so hide them entirely
                    // and bake PWD into every tick.
                    defaultLevel = "PWD";
                    hideLevelPicker = true;
                  }
                }
                return (
                  <div
                    key={t.id}
                    role="tabpanel"
                    data-testid={`rc-tabpanel-${t.id}`}
                    className={effectiveTab === t.id ? "" : "hidden"}
                  >
                    <SubjectCard
                      subject={subj}
                      grade={grade}
                      reportingPeriod={period}
                      reportType={reportType}
                      defaultLevel={defaultLevel}
                      hideLevelPicker={hideLevelPicker}
                      defaultOpen
                      onCommentChange={handleCommentChange(t.id)}
                      activityMeta={activityMeta}
                      onActivityChange={load}
                      charLimit={subjectCharLimits[t.subject] ?? DEFAULT_SUBJECT_CHAR_LIMIT}
                    />
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Item 2 — persistent FAB. Only after at least one expectation is
          marked TAUGHT does generating a Live Report become meaningful.
          Respects safe-area insets (mobile home-bar) and sits above the
          horizontal tab scroll without overlapping it. */}
      {canLiveReports && hasAnyTaught && !inLiveReports ? (
        <button
          type="button"
          onClick={openLiveReports}
          data-testid="rc-fab-live-reports"
          aria-label="Generate Live Report Comments"
          className="fixed z-40 right-4 sm:right-6 bg-[#1c9e7e] hover:bg-[#177a61] text-white font-extrabold rounded-full shadow-xl px-4 py-3 inline-flex items-center gap-2 transition-colors text-sm"
          style={{
            // Sit above mobile safe-area + above the typical 56-72px
            // bottom navigation height on phones.
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)",
          }}
        >
          <Wand2 className="h-4 w-4" />
          <span className="hidden sm:inline">Generate Live Report Comments</span>
          <span className="sm:hidden">Live Reports</span>
          <span aria-hidden="true">→</span>
        </button>
      ) : null}
    </div>
  );
};

export default ReportCardGenerator;
